import { createTheme, ThemeOptions } from '@mui/material'
import '@mui/material/styles'
import Link from 'next/link'
import resolveConfig from 'tailwindcss/resolveConfig'
import tailwindConfig from '../../../tailwind.config'
import { COLOR_PALETTE } from './colors'

const fullConfig = resolveConfig(tailwindConfig)

const rootElement =
  typeof window !== 'undefined'
    ? document.querySelector(
        ':is(#__next, #headlessui-portal-root, #notification-root, body.sb-show-main)'
      )
    : null

const theme = createTheme({
  breakpoints: {
    values: {
      xs: 0,
      sm: 580,
      md: 768,
      lg: 1008,
      xl: 1280,
      '2xl': 1536,
    },
  },
})

export const MARKETPLACE_THEME: ThemeOptions = createTheme({
  ...theme,
  palette: COLOR_PALETTE,
  typography: {
    fontFamily: 'var(--font-inter)',
    h1: {
      fontSize: '1.5rem',
      fontWeight: 400,
      lineHeight: '120%',
      letterSpacing: '-0.8px',
      fontFamily: 'var(--font-gt-planar)',
      color: '#000000',
      [theme.breakpoints.up('md')]: {
        fontSize: '2.4rem',
      },
    },
    h2: {
      fontSize: '1.25rem',
      fontWeight: 400,
      lineHeight: '130%',
      fontFamily: 'var(--font-gt-planar)',
      color: '#000000',
      [theme.breakpoints.up('md')]: {
        fontSize: '1.875rem',
      },
    },
    h3: {
      fontSize: '1.375rem',
      fontWeight: 600,
      lineHeight: '140%',
      letterSpacing: '-0.6px',
      color: '#000000',
      [theme.breakpoints.up('md')]: {
        fontSize: '1.875rem',
      },
    },
    h4: {
      fontSize: 24,
      fontWeight: 400,
      lineHeight: '130%',
      fontFamily: 'var(--font-gt-planar)',
      color: '#000',
      letterSpacing: '-0.48px',
    },
    h5: {
      fontSize: 24,
      fontWeight: 600,
      lineHeight: '125%',
      color: '#000',
    },
    h6: {
      fontSize: 20,
      fontWeight: 400,
      lineHeight: '140%',
      letterSpacing: '-0.4px',
      fontFamily: 'var(--font-gt-planar)',
      color: '#000',
    },
    subtitle1: {
      fontSize: 22,
      lineHeight: '130%',
      color: '#000',
    },
    subtitle2: {
      fontSize: 18,
      fontWeight: 600,
      lineHeight: '155%',
      color: '#000',
    },
    subtitle3: {
      fontSize: 18,
      fontFamily: 'var(--font-gt-planar)',
      fontWeight: 400,
      lineHeight: '130%',
      color: '#000',
    },
    subtitle4: {
      fontSize: 14,
      fontWeight: 600,
      lineHeight: '130%',
      color: '#000',
    },
    body1: {
      fontSize: 16,
      lineHeight: '150%',
      color: '#000',
    },
    body2: {
      fontFamily: 'var(--font-gt-planar)',
      fontSize: 16,
      lineHeight: '137.5%',
    },
    body3: {
      fontFamily: 'var(--font-inter)',
      fontSize: 14,
      lineHeight: '130.0%',
    },
    body4: {
      fontFamily: 'var(--font-gt-planar)',
      fontSize: 14,
      lineHeight: '127.571%',
      component: 'p',
    },
    caption: {
      fontSize: 12,
      lineHeight: '133.333%',
    },
  },
  components: {
    MuiTextField: {
      defaultProps: {
        variant: 'outlined',
      },
      styleOverrides: {
        root: {
          '& .MuiInputBase-input': {
            appearance: 'none !important',
            border: 'none !important',
            outline: 'none !important',
            '&:focus': {
              boxShadow: 'none !important',
              border: 'none !important',
            },
          },
        },
      },
    },
    MuiPopover: {
      defaultProps: {
        container: rootElement,
      },
      styleOverrides: {
        root: {
          '& .MuiBackdrop-root': {
            backgroundColor: 'transparent',
          },
          '& .MuiList-root': {
            padding: 0,
          },
          '& .MuiMenuItem-root': {
            borderBottom: '1px solid',
            borderColor: fullConfig.theme.colors['dividers'],
            padding: '8px 16px',
            '&:last-child': {
              borderBottom: 'none',
            },
          },
        },
        paper: {
          boxShadow: fullConfig.theme.boxShadow['user-menu'],
          border: '1px solid',
          borderColor: fullConfig.theme.colors['dividers'],
          borderRadius: 8,
        },
      },
    },
    MuiMenu: {
      defaultProps: {
        container: rootElement,
        classes: {
          list: 'py-2',
        },
      },
    },
    MuiMenuItem: {
      defaultProps: {
        classes: {
          root: 'text-sm font-medium text-mydra-black border-none hover:bg-mydra-light-purple px-4',
          selected: 'text-mydra-purple bg-transparent',
        },
      },
    },
    MuiPopper: {
      defaultProps: {
        container: rootElement,
      },
    },
    MuiButton: {
      defaultProps: {
        variant: 'contained',
        size: 'small',
        classes: {
          root: 'normal-case rounded-lg font-medium text-base p-4 shadow-none',
          sizeSmall: 'px-3 py-2 text-sm',
          contained:
            'shadow-none hover:shadow-none bg-mydra-black hover:bg-mydra-black/90',
          text: 'hover:opacity-100',
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'capitalize',
          fontSize: 16,
          fontWeight: 600,
          color: COLOR_PALETTE.text.primary,
          paddingLeft: 0,
          paddingRight: 0,
        },
      },
    },
    MuiTabs: {
      defaultProps: {
        classes: {
          root: 'border-b border-dividers',
          flexContainer: 'gap-10',
        },
      },
    },
    MuiButtonGroup: {
      styleOverrides: {
        root: {
          borderRadius: '8px',
        },
      },
    },
    MuiPaper: {
      defaultProps: {
        elevation: 0,
        square: true,
      },
    },
    MuiModal: {
      defaultProps: {
        container: rootElement,
      },
      styleOverrides: {
        root: {
          '& .MuiBackdrop-root': {
            backgroundColor: 'rgba(51, 36, 76, 0.40)', // mydra-dark-purple with 40% opacity
          },
        },
      },
    },
    MuiOutlinedInput: {
      defaultProps: {
        classes: {
          root: 'rounded-lg',
        },
      },
      styleOverrides: {
        root: {
          '&.Mui-disabled': {
            backgroundColor: fullConfig.theme.colors['gray-background'],
          },
        },
      },
    },
    MuiDrawer: {
      defaultProps: {
        container: rootElement,
      },
    },
    MuiDialog: {
      defaultProps: {
        container: rootElement,
      },
      styleOverrides: {
        root: {
          '& .MuiDialog-paper': {
            paddingBlock: '24px',
            paddingInline: '16px',
            paddingBlockEnd: '12px',
            borderRadius: '12px',
            maxWidth: 'none',
            width: 'auto',
            margin: '0',
            position: 'relative',
          },
          '& .MuiDialogTitle-root': {
            padding: '0',
            marginBottom: '6px',
            '& h2': {
              fontSize: '1.5rem',
              lineHeight: '2rem',
              fontWeight: '700',
              wordBreak: 'break-word',
              whiteSpace: 'pre-wrap',
            },
            '& h3': {
              fontSize: '0.875rem',
              lineHeight: '1.5rem',
              color: 'text.secondary',
            },
          },
          '& .MuiDialogContent-root': {
            padding: '0',
            marginTop: '24px',
            overflow: 'hidden',
          },
          '& .MuiDialogActions-root': {
            padding: '0',
            marginTop: '24px',
          },
        },
      },
    },
    MuiDialogTitle: {
      defaultProps: {
        classes: 'text-2xl',
      },
    },
    MuiSnackbar: {
      defaultProps: {
        anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
      },
      styleOverrides: {
        root: {
          backgroundColor: 'white',
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          color: COLOR_PALETTE.text.primary,
          fontWeight: 500,
          fontSize: '14px',
          marginBottom: '8px',
        },
      },
    },
    MuiLink: {
      defaultProps: {
        component: Link,
      },
    },
    MuiButtonBase: {
      defaultProps: {
        LinkComponent: Link,
      },
    },
    MuiSwitch: {
      defaultProps: {
        color: 'secondary',
      },
    },
    MuiRadio: {
      defaultProps: {
        color: 'secondary',
      },
    },
    MuiCheckbox: {
      defaultProps: {
        color: 'secondary',
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          '.MuiTableCell-head': {
            fontSize: 14,
            fontWeight: 600,
          },
          '.Mui-TableHeadCell-Content': {
            justifyContent: 'space-between',
            gap: '.5rem',
          },
          '.MuiTableCell-alignRight .Mui-TableHeadCell-Content': {
            justifyContent: 'end',
          },
          '.MuiTableCell-alignCenter .Mui-TableHeadCell-Content': {
            justifyContent: 'center',
          },
          '.Mui-TableHeadCell-Content-Labels': {
            gap: '.5rem',
          },
          '.MuiTableCell-alignRight .Mui-TableHeadCell-Content-Actions': {
            order: -1,
          },
          '.MuiTableRow-head': {
            boxShadow: 'none',
          },
        },
      },
    },
    MuiTableContainer: {
      styleOverrides: {
        root: {
          boxShadow: 'none',
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: COLOR_PALETTE.background.paper,
          border: 'none',
          borderRadius: 8,
          boxShadow: fullConfig.theme.boxShadow['user-menu'],
          fontSize: 12,
          padding: 8,
          color: COLOR_PALETTE.text.primary,
        },
        arrow: {
          color: COLOR_PALETTE.background.paper,
        },
      },
    },
    MuiDateCalendar: {
      styleOverrides: {
        root: {
          '& .MuiDayCalendar-weekDayLabel': {
            color: COLOR_PALETTE.text.disabled,
          },
          '& .MuiPickersArrowSwitcher-button': {
            color: COLOR_PALETTE.primary.main,
          },
          '& .MuiPickersDay-root.Mui-selected, & .MuiPickersDay-root.Mui-selected:focus':
            {
              backgroundColor: COLOR_PALETTE.secondary.main,
            },
          '& .MuiPickersDay-root.Mui-selected:hover': {
            backgroundColor: COLOR_PALETTE.secondary.dark,
          },
          '& .MuiPickersDay-root.MuiPickersDay-today': {
            borderColor: COLOR_PALETTE.secondary.main,
          },
        },
      },
    },
    MuiPickersDay: {
      styleOverrides: {
        root: {
          '&.Mui-selected': {
            backgroundColor: COLOR_PALETTE.primary.main,
            '&:hover': {
              backgroundColor: COLOR_PALETTE.primary.light,
            },
          },
        },
      },
    },
    MuiChip: {
      defaultProps: {
        classes: {
          sizeSmall: 'text-sm font-semibold first-letter:capitalize',
        },
      },
    },
  },
} as ThemeOptions)
